import React, { Component } from 'react';
import { string, func, bool } from 'prop-types';
import { createSelector } from 'reselect';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { isEmpty } from '@widergy/web-utils/lib/array';

import { ROUTES } from 'constants/routes';
import SecondaryTopbar from 'app/components/SecondaryTopbar';
import UTDownloadBill from 'app/components/UTDownloadBill';
import AccountSelect from 'app/components/AccountSelect';
import withPagination from 'app/components/UTPagination/HOCs/pagination';
import WithoutAccounts from 'app/components/WithoutAccounts';
import BillActions from 'redux/bill/actions';
import { appConfig } from 'config/customization';
import { billPropTypes } from 'types/billTypes';
import { actionCreators as accountActions, accountPropTypes } from 'redux/accountHandlers';
import { message } from 'utils/stringUtils';

import { gaTrackPageView } from '../../../../../services/AnalyticsService';

import { billForView } from './utils';
import BillsScene from './layout';
import stylesJS from './styles';

const initialPageNumber = 1;

const SELECTOR_LABELS = ['client_number', 'address'];

class BillsSceneContainer extends Component {
  componentDidMount() {
    gaTrackPageView(ROUTES.BILLS);
    this.props.onMount(appConfig.billsPageSize);
  }

  componentDidUpdate(prevProps) {
    // When you change account in this component
    if (prevProps.currentAccount && prevProps.currentAccount.id !== this.props.currentAccount.id) {
      this.props.onMount(appConfig.billsPageSize);
    }
  }

  handleChangeAccount = account => this.props.changeAccount(account);

  handleMultipleDownload = () => this.props.onClickMultipleDownload(this.props.currentAccount);

  render() {
    return (
      <div style={stylesJS.container}>
        <SecondaryTopbar titleText={message('Bills:list')} style={stylesJS.topbar}>
          {appConfig.bills.showAccountSelector && (
            <AccountSelect
              filterPlaceholder={message('AccountStatus:filterPlaceholder')}
              selectedOption={this.props.currentAccount}
              dualLabel={SELECTOR_LABELS}
              options={this.props.accounts}
              onSelect={this.handleChangeAccount}
              keyProp="id"
              style={stylesJS.selectContainer}
            />
          )}
          {appConfig.multipleBillsDownload && (
            <UTDownloadBill
              loadingMessage={message('Bills:multipleBillsLoading')}
              title={message('Bills:multipleBillsTitle')}
              downloadUrl={this.props.multipleBillsUrl}
              onClickDownload={this.handleMultipleDownload}
              useIcon={appConfig.bills.downloadIcon}
              labelButton={message('Bills:multipleDownload')}
              disabled={isEmpty(this.props.rows)}
            />
          )}
        </SecondaryTopbar>
        <BillsScene
          loading={this.props.loading}
          bills={this.props.rows}
          onClickDownload={this.props.onClickDownload}
          printedBill={this.props.printedBill}
          onRowsRendered={this.props.onRowsRendered}
          onBillPayment={this.handleBillPayment}
          appending={this.props.appending}
          onSelectBill={this.props.onSelectBill}
        />
      </div>
    );
  }
}

const billsSelector = createSelector(
  store => store.bill.bills,
  bills => bills.map(billForView)
);

const mapStateToProps = store => ({
  rows: billsSelector(store),
  loading: store.bill.billsLoading,
  printedBill: store.bill.printedBill && store.bill.printedBill.URL_FILE_BILL,
  accounts: store.account.accounts,
  accountsLoading: store.account.accountsLoading,
  currentAccount: store.account.currentAccount,
  multipleBillsUrl: store.bill.multipleBills,
  appending: store.bill.appendBillsLoading,
  accountsError: store.account.accountsError
});

const mapDispatchToProps = dispatch => ({
  onMount: pageSize => dispatch(BillActions.getBills(initialPageNumber, pageSize)),
  append: (pageNumber, pageSize) => dispatch(BillActions.appendBills(pageNumber, pageSize)),
  onClickDownload: billId =>
    dispatch(
      appConfig.bills.billDownloadOnPDF
        ? BillActions.getPrintedBillPDF(billId)
        : BillActions.getPrintedBill(billId, 'bills')
    ),
  onClickMultipleDownload: account => dispatch(BillActions.getMultipleBills(account)),
  changeAccount: account => dispatch(accountActions.changeCurrentAccount(account)),
  onSelectBill: bill => dispatch(push({ pathname: ROUTES.BILLS_DETAIL, params: bill.id }))
});

BillsSceneContainer.defaultProps = {
  printedBill: null
};

BillsSceneContainer.propTypes = {
  loading: bool,
  rows: billPropTypes,
  printedBill: string,
  onClickDownload: func,
  onClickMultipleDownload: func,
  onRowsRendered: func,
  changeAccount: func.isRequired,
  onSelectBill: func.isRequired,
  onMount: func.isRequired,
  accounts: accountPropTypes,
  currentAccount: accountPropTypes,
  multipleBillsUrl: string,
  appending: bool
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  WithoutAccounts({
    shouldShowMessage: props => props.accounts.length === 0 && !props.accountsLoading,
    accountsError: props => props.accountsError
  })(withPagination(BillsSceneContainer, appConfig.billsPageSize, appConfig.disableBillsPagination, false))
);
